import { HELP_CENTER_URL } from '@aloft/toolkit-shared';
import { Flex, Icon, IconProps, Link, Typography, TypographyChildren } from '@aloft/toolkit-sui';

import { QaAttempt } from '../types.ts';

const AlertHeader = (props: { iconProps: IconProps; children: TypographyChildren }) => {
  const { iconProps, children } = props;

  return (
    <Flex gap="4px">
      <span style={{ marginTop: '3px' }}>
        <Icon {...iconProps} />
      </span>
      <Typography>{children}</Typography>
    </Flex>
  );
};

type ResultsSectionProps = { qaAttempt: QaAttempt };

export const ResultsSection = (props: ResultsSectionProps) => {
  const { qaAttempt } = props;
  const { status } = qaAttempt;

  if (status === 'in-progress') {
    return (
      <AlertHeader
        iconProps={{
          icon: 'information',
          color: 'alertWarning',
        }}
      >
        Your report is being checked. We&apos;ll email you when your results are ready.
      </AlertHeader>
    );
  }

  if (status === 'error') {
    return (
      <AlertHeader
        iconProps={{
          icon: 'error-warning',
          color: 'alertError',
        }}
      >
        We weren&apos;t able to check your report. You may have uploaded incorrect or invalid
        documents. To retry, please create a new order or see our&nbsp;
        <Link href={HELP_CENTER_URL} isExternal>
          Help Center
        </Link>
        &nbsp;to contact us.
      </AlertHeader>
    );
  }

  if (status === 'success') {
    const { results } = qaAttempt;

    if (!results?.length) {
      return (
        <AlertHeader
          iconProps={{
            icon: 'information',
            color: 'alertSuccess',
          }}
        >
          Your report was checked and no issues were identified.
        </AlertHeader>
      );
    }

    return (
      <Flex flexDirection="column" gap="8px">
        <AlertHeader
          iconProps={{
            icon: 'information',
            color: 'alertWarning',
          }}
        >
          {(results ?? [])?.length.toLocaleString()} potential issues found:
        </AlertHeader>
        <ol
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {results?.map((result, i) => (
            <li key={`${result}-${i}`}>
              <Flex gap="4px">
                {/* TODO: Possibly create SUI `<ol>` and `<li>` components that let us
                                    control `list-style-type`, or audit our existing uses of native list
                                    elements and update our global styles.
                                    In the meantime, pretend like you don't see this. */}
                <Typography>{(i + 1).toLocaleString()}.</Typography>
                <Typography>{result}</Typography>
              </Flex>
            </li>
          ))}
        </ol>
      </Flex>
    );
  }

  throw new Error(`Invalid QA status '${status}'`);
};
